import { Component, type ReactNode, type ErrorInfo } from "react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = "/dashboard";
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full rounded-lg border bg-card p-8 shadow-lg">
          <div className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-destructive/10">
            <svg
              className="w-7 h-7 text-destructive"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h1 className="text-xl font-semibold text-foreground text-center mb-2">
            Etwas ist schiefgelaufen
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-6">
            Ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite
            neu oder kehren Sie zum Dashboard zurück.
          </p>

          {import.meta.env.DEV && this.state.error && (
            <div className="mb-6 rounded-md border bg-muted/50 p-4">
              <p className="text-sm font-medium text-foreground mb-1">
                Fehlerdetails
              </p>
              <p className="text-xs font-mono text-destructive mb-2">
                {this.state.error.toString()}
              </p>
              {this.state.errorInfo && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer font-medium mb-1">
                    Component Stack
                  </summary>
                  <pre className="whitespace-pre-wrap overflow-auto max-h-40 mt-1 p-2 rounded border bg-background">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Button onClick={this.handleReload} className="w-full">
              Seite neu laden
            </Button>
            <Button
              onClick={this.handleGoHome}
              variant="outline"
              className="w-full"
            >
              Zum Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
